import * as React from 'react';
import { Link } from 'react-router-dom';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import {
  PageTitle, Card, EmptyState, Btn, Pill, Section,
  Table, Th, Td, fmt,
} from '../components/ui-kit';
import { Search } from 'lucide-react';

interface Mission {
  id: string;
  title: string;
  status: string;
  seniority?: string;
  city_zone?: string;
  tjm_band?: string;
  created_at?: string;
}

export default function Missions() {
  const { getToken } = useXyqarAuth();
  const [items, setItems] = React.useState<Mission[] | null>(null);
  const [q, setQ] = React.useState('');
  const [status, setStatus] = React.useState<string>('');

  React.useEffect(() => {
    (async () => {
      try {
        const tok = await getToken().catch(() => '');
        // API returns { items: [...] } not { missions: [...] }
        const j = await apiGet<{ items: Mission[] }>('/api/v1/client/missions', tok);
        setItems(j.items ?? []);
      } catch { setItems([]); }
    })();
  }, [getToken]);

  const filtered = (items ?? []).filter((m) => {
    if (status && m.status !== status) return false;
    if (q && !(`${m.title} ${m.id}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const counts = (items ?? []).reduce<Record<string, number>>((acc, m) => {
    acc[m.status] = (acc[m.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <PageTitle
        kicker="Active engagements"
        title="Missions"
        right={<Pill tone="live">{fmt(items?.length)} total</Pill>}
      />

      <Section
        title="Filters"
        right={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-md text-xs text-white outline-none focus:border-white/30 w-48"
              />
            </div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-md text-xs text-white px-2 py-1.5 outline-none focus:border-white/30"
            >
              <option value="">all status</option>
              {Object.keys(counts).map((s) => <option key={s} value={s}>{s} ({counts[s]})</option>)}
            </select>
          </div>
        }
      >
        {items === null && <Card>Loading…</Card>}
        {items && filtered.length === 0 && <EmptyState title="No mission matches your filter" hint="Adjust search or status" />}
        {items && filtered.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Mission</Th>
                <Th>Seniority</Th>
                <Th>Zone</Th>
                <Th>TJM band</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-t border-white/5 hover:bg-white/5 transition">
                  <Td>
                    <Link to={`/missions/${m.id}`} className="text-white hover:underline">{m.title}</Link>
                    <div className="text-[10px] text-white/30 font-mono">{m.id}</div>
                  </Td>
                  <Td><span className="text-xs uppercase font-mono text-white/60">{m.seniority ?? '—'}</span></Td>
                  <Td><span className="text-xs font-mono text-white/60">{m.city_zone || '—'}</span></Td>
                  <Td><span className="text-xs font-mono text-white/60">{m.tjm_band ?? '—'}</span></Td>
                  <Td><Pill tone={m.status === 'matched' ? 'live' : 'mute'}>{m.status}</Pill></Td>
                  <Td><span className="text-[11px] text-white/40 font-mono">{m.created_at?.slice(0, 10)}</span></Td>
                  <Td className="text-right"><Link to={`/missions/${m.id}`}><Btn>Open</Btn></Link></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </div>
  );
}
